import { useState, useEffect } from 'react';
import { uploadSignature, getOwnSignature, removeSignature, ProfileSignatureResponse } from '../services/profile';

/**
 * Hook return type
 */
interface UseProfileSignatureReturn {
  /** Current signature base64 string (or null if none) */
  signature: string | null;
  
  /** Loading state when fetching signature */
  loading: boolean;
  
  /** Uploading state when uploading signature */
  uploading: boolean;
  
  /** Error message (null if no error) */
  error: string | null;
  
  /** Upload a new signature */
  uploadSignature: (file: File) => Promise<void>;
  
  /** Remove current signature */
  removeSignature: () => Promise<void>;
  
  /** Refetch signature from server */
  refetch: () => Promise<void>;
}

/**
 * Custom hook to manage user's profile signature
 * 
 * Handles fetching, uploading, and removing signature images.
 * Automatically fetches signature on mount.
 * 
 * @example
 * ```tsx
 * const { signature, loading, uploading, error, uploadSignature, removeSignature } = useProfileSignature();
 * 
 * if (loading) return <CircularProgress />;
 * 
 * return (
 *   <SignatureUpload
 *     currentSignature={signature}
 *     onUpload={uploadSignature}
 *     onRemove={removeSignature}
 *     uploading={uploading}
 *     error={error}
 *   />
 * );
 * ```
 */
export const useProfileSignature = (): UseProfileSignatureReturn => {
  const [signature, setSignature] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch signature from server
   */
  const fetchSignature = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await getOwnSignature();
      
      // 404 means no signature uploaded yet - this is OK
      if (response.status === 404) {
        setSignature(null);
      } else if (response.signatureBase64) {
        setSignature(response.signatureBase64);
      } else {
        setSignature(null);
      }
    } catch (err: any) {
      console.error('Error fetching signature:', err);
      // Don't set error for 404 (no signature) - it's expected
      if (!err.message.includes('404')) {
        setError(err.message || 'Failed to load signature');
      }
      setSignature(null);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Upload a new signature
   */
  const handleUploadSignature = async (file: File): Promise<void> => {
    try {
      setUploading(true);
      setError(null);
      
      await uploadSignature(file);
      
      // After successful upload, fetch the new signature
      await fetchSignature();
    } catch (err: any) {
      console.error('Error uploading signature:', err);
      setError(err.message || 'Failed to upload signature');
      throw err; // Re-throw so component can handle it
    } finally {
      setUploading(false);
    }
  };

  /**
   * Remove current signature
   */
  const handleRemoveSignature = async (): Promise<void> => {
    try {
      setUploading(true);
      setError(null);
      
      await removeSignature();
      
      // Clear signature from state
      setSignature(null);
    } catch (err: any) {
      console.error('Error removing signature:', err);
      setError(err.message || 'Failed to remove signature');
      throw err; // Re-throw so component can handle it
    } finally {
      setUploading(false);
    }
  };

  /**
   * Refetch signature (useful after upload/remove)
   */
  const refetch = async (): Promise<void> => {
    await fetchSignature();
  };

  // Fetch signature on mount
  useEffect(() => {
    fetchSignature();
  }, []);

  return {
    signature,
    loading,
    uploading,
    error,
    uploadSignature: handleUploadSignature,
    removeSignature: handleRemoveSignature,
    refetch,
  };
};

export default useProfileSignature;

